import React, { useContext, useEffect, useState } from 'react';

import { Button, Col, Divider, Flex, message, Row } from 'antd';
import MonitoringTile from './MonitoringTile';
import { DatabaseOutlined, ReloadOutlined, SafetyOutlined, ThunderboltOutlined, VideoCameraOutlined } from '@ant-design/icons';
import HttpClient from '../../../../core/HttpClient';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';
import { formatDuration } from '@leedpulse-monorepo/utils';

const HEALTHCHECK_URL = import.meta.env.VITE_SERVICE_HEALTHCHECK_URL;

interface HealthcheckThresholds {
    latency_ok_ms: number;
    latency_warn_ms: number;
    latency_alert_ms: number;
    ram_ok_mb: number;
    ram_warn_mb: number;
    log_rate_alert_per_min: number;
}

const DEFAULT_THRESHOLDS: HealthcheckThresholds = {
    latency_ok_ms: 200,
    latency_warn_ms: 1000,
    latency_alert_ms: 3000,
    ram_ok_mb: 512,
    ram_warn_mb: 1024,
    log_rate_alert_per_min: 2000,
};

const latencyColor = (ms: number, t: HealthcheckThresholds): string => (ms <= t.latency_ok_ms ? 'green' : ms <= t.latency_warn_ms ? 'orange' : 'red');
const ramColor = (mb: number, t: HealthcheckThresholds): string => (mb <= t.ram_ok_mb ? 'green' : mb <= t.ram_warn_mb ? 'orange' : 'red');

const SENTRY_LEED_GROUP_URL = import.meta.env.VITE_SENTRY_LEED_GROUP_URL;
const SENTRY_BACKEND_DSN = import.meta.env.VITE_SENTRY_BACKEND_DSN;
const SENTRY_FRONTEND_DSN = import.meta.env.VITE_SENTRY_FRONTEND_DSN;
const SENTRY_FRONTENDLEEDFLOW_DSN = import.meta.env.VITE_SENTRY_FRONTENDLEEDFLOW_DSN;
const SENTRY_BACKOFFICE_DSN = import.meta.env.VITE_SENTRY_BACKOFFICE_DSN;
const SENTRY_MOBILE_LEEDFLOW_DSN = import.meta.env.VITE_SENTRY_MOBILE_LEEDFLOW_DSN;

type HealthcheckStatusApi = 'OK' | 'ERROR' | null;
interface HealthcheckServiceStatusDto {
    name: string;
    lastStatus: HealthcheckStatusApi;
    lastCheckDate: number | null; // ISO
    nextCheckDate: number | null; // ISO
    lastStatusChangeDate: number | null; // ISO
    responseTimeMs: number | null;
    logsLastMinute: number | null;
    memory: { rss: number; heapUsed: number; heapTotal: number } | null;
}

interface HealthcheckStatusResponseDto {
    services: HealthcheckServiceStatusDto[];
    thresholds?: HealthcheckThresholds;
}

export enum MonitoringItemStatus {
    ONLINE = 'online',
    OFFLINE = 'offline',
}

export interface MonitoringItem {
    key: string;
    title: string;
    status: MonitoringItemStatus;
    value: number | string | React.ReactNode;
    description?: string;
    since?: number;
    redirectUrl?: string;
    onClick?: (value: number | string | React.ReactNode) => void;
    color?: string;
}

// Au niveau module : l'etat initial de monitoringItems l'appelle, et un `const`
// declare dans le composant serait dans sa zone morte a ce moment-la. Elle ne
// capture que des constantes de module, donc rien ne la retenait dedans.
const getSentryServiceURL = (serviceName: string, sentryServiceDsn: string) => {
    const sentryServiceId = sentryServiceDsn.split('/').pop(); // Service ID is the last part of the DSN url
    return `${SENTRY_LEED_GROUP_URL}/insights/projects/${serviceName}/?project=${sentryServiceId}`;
};

interface MonitoringItems {
    [key: string]: MonitoringItem[];
}

export default function Monitoring() {
    const authContext = useContext(AuthenticationContext);

    // Etat de depart des deux tableaux : un effet de montage sur [] faisait le meme
    // travail un rendu trop tard, et affichait un tableau vide entre les deux.
    const [monitoringItems, setMonitoringItems] = useState<MonitoringItems>({
            healthCheck: [
                {
                    key: 'backend',
                    title: 'Backend',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: `${HEALTHCHECK_URL}/status`,
                },
            ],
            // Sentry statusUrl not specified for now
            sentry: [
                {
                    key: 'backend',
                    title: 'api.leedflow.com',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: getSentryServiceURL('backend', SENTRY_BACKEND_DSN),
                },
                {
                    key: 'frontend-leedpulse',
                    title: 'app.leedpulse.com',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: getSentryServiceURL('frontend-leedpulse', SENTRY_FRONTEND_DSN),
                },
                {
                    key: 'frontend-leedflow',
                    title: 'app.leedflow.com',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: getSentryServiceURL('frontend-leedflow', SENTRY_FRONTENDLEEDFLOW_DSN),
                },
                {
                    key: 'backoffice-leedpulse',
                    title: 'admin.leedpulse.com',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: getSentryServiceURL('backoffice-leedpulse', SENTRY_BACKOFFICE_DSN),
                },
                {
                    key: 'mobile-leedflow',
                    title: 'Mobile',
                    status: MonitoringItemStatus.OFFLINE,
                    value: "N/A",
                    redirectUrl: getSentryServiceURL('mobile-leedflow', SENTRY_MOBILE_LEEDFLOW_DSN),
                },
            ]
    });
    const [isHealthCheckLoading, setIsHealthCheckLoading] = React.useState<boolean>(false);
    const [isSentryLoading, setIsSentryLoading] = React.useState<boolean>(false);

    /**
     * Merge fetched updates into existing tiles without wiping initial metadata (title, redirectUrl, onClick, color, ...).
     * - Existing items are updated (shallow-merge) when their `key` is present in updates.
     * - Unknown keys are appended at the end (optional).
     * 
     * --> This make the backoffice able to show status for services that aren't defined in this file but returned by the backend, without redeploying
     */
    const mergeMonitoringItemsByKey = (
        existing: MonitoringItem[],
        updates: Array<Pick<MonitoringItem, 'key'> & Partial<MonitoringItem>>,
        options?: { appendNew?: boolean }
    ): MonitoringItem[] => {
        const updatesByKey = new Map(updates.map((u) => [u.key, u] as const));
        const next = existing.map((item) => {
            const update = updatesByKey.get(item.key);
            if (!update) return item;
            return {
                ...item,
                ...update,
                // Enforce stable identity fields
                key: item.key.toLowerCase(),
                title: update.title ?? item.title,
            };
        });

        if (!options?.appendNew) return next;

        const existingKeys = new Set(existing.map((i) => i.key));
        const appended = updates
            .filter((u) => !existingKeys.has(u.key))
            .map((u) => ({
                key: u.key.toLowerCase(),
                title: u.title ?? u.key,
                status: u.status ?? MonitoringItemStatus.OFFLINE,
                value: u.value ?? "N/A",
                since: u.since,
                redirectUrl: u.redirectUrl,
                onClick: u.onClick,
                color: u.color,
            }) satisfies MonitoringItem);

        return [...next, ...appended];
    };



    const fetchHealthchecksStatus = async (): Promise<HealthcheckStatusResponseDto | null> => {
        try {
            setIsHealthCheckLoading(true);
            const res = await fetch(`${HEALTHCHECK_URL}/status`);
            if (!res.ok) return null;

            const json = (await res.json()) as HealthcheckStatusResponseDto;
            if (!json?.services || !Array.isArray(json.services)) return null;

            return json;
        } catch {
            message.error('Error fetching healthcheck status');
            return null;
        } finally {
            setIsHealthCheckLoading(false);
        }
    };

    const fetchSentryStatus = async (): Promise<Record<string, { error: number }> | null> => {
        try {
            setIsSentryLoading(true);
            const res = await HttpClient.get(`/sentry/events/count`, authContext.authentication?.token);
            if (!res.success) return null;

            return res.data;
        } catch {
            message.error('Error fetching sentry status');
            return null;
        } finally {
            setIsSentryLoading(false);
        }
    };

    /**
     * @param items Array of MonitoringItem
     * @returns Items component by row of 3
     */
    const getMonitoringItemsRows = (items: MonitoringItem[], isLoading: boolean) => {
        const rows = [];
        for (let i = 0; i < items.length; i += 3) {
            rows.push(
                <Row key={i} gutter={16}>
                    {items.slice(i, i + 3).map((item, index) => (
                        <Col key={index} span={8}>
                            <MonitoringTile isLoading={isLoading} item={item} />
                        </Col>
                    ))}
                </Row>
            );
        }
        return rows;
    }

    const refreshHealthcheckStatus = async () => {
        const response = await fetchHealthchecksStatus();
        if (!response) return;

        const { services } = response;
        const thresholds = response.thresholds ?? DEFAULT_THRESHOLDS;

        const updates = services.map((service) => {
            const isOnline = service.lastStatus === 'OK';
            const since = service.lastStatusChangeDate ? Date.now() - service.lastStatusChangeDate : 0;
            const latency = service.responseTimeMs;
            const ramMb = service.memory ? Math.round(service.memory.rss / (1024 * 1024)) : null;

            const value: React.ReactNode = isOnline && latency !== null
                ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                        <span style={{ color: latencyColor(latency, thresholds) }}>
                            <ThunderboltOutlined style={{ marginRight: 6, fontSize: '0.8em' }} />
                            {latency}<span style={{ fontSize: '0.5em', color: '#949494', marginLeft: 3 }}>ms</span>
                        </span>
                        {ramMb !== null && (
                            <span style={{ color: ramColor(ramMb, thresholds) }}>
                                <DatabaseOutlined style={{ marginRight: 6, fontSize: '0.8em' }} />
                                {ramMb}<span style={{ fontSize: '0.5em', color: '#949494', marginLeft: 3 }}>Mo</span>
                            </span>
                        )}
                    </span>
                )
                : (isOnline ? MonitoringItemStatus.ONLINE : MonitoringItemStatus.OFFLINE);

            const description: string | undefined = isOnline && latency !== null
                ? undefined
                : (since > 0 ? `Depuis ${formatDuration(since)}` : undefined);

            return {
                key: service.name.toLowerCase(),
                title: service.name,
                status: isOnline ? MonitoringItemStatus.ONLINE : MonitoringItemStatus.OFFLINE,
                value,
                description,
                color: undefined,
                since,
                redirectUrl: `${HEALTHCHECK_URL}/interface`,
            };
        });

        setMonitoringItems((prev) => ({
            ...prev,
            healthCheck: mergeMonitoringItemsByKey(prev.healthCheck, updates, { appendNew: true }),
        }));
    };

    const refreshSentryStatus = async () => {
        const sentryItemsResponse: Record<string, { error: number }> | null = await fetchSentryStatus();
        if (!sentryItemsResponse) return;

        const updates = Object.entries(sentryItemsResponse).map(([key, value]) => ({
            key: key.toLowerCase(),
            status: value.error > 5 ? MonitoringItemStatus.OFFLINE : MonitoringItemStatus.ONLINE,
            value: value.error,
            description: `erreurs aujourd'hui`,
            redirectUrl: getSentryServiceURL(key, SENTRY_BACKEND_DSN),
        }));

        setMonitoringItems((prev) => ({
            ...prev,
            sentry: mergeMonitoringItemsByKey(prev.sentry, updates, { appendNew: true }),
        }));
    };


    return (
        <Flex vertical gap={16}>
            <h2 >
                <SafetyOutlined style={{ marginRight: 6 }} /> Health Check
                <Button icon={<ReloadOutlined />} loading={isHealthCheckLoading} onClick={refreshHealthcheckStatus} shape="circle" style={{ marginLeft: '1rem' }} />
            </h2>

            {getMonitoringItemsRows(monitoringItems.healthCheck, isHealthCheckLoading)}

            <Divider orientation='left' />
            <h2>
                <VideoCameraOutlined style={{ marginRight: 6 }} /> Sentry
                <Button icon={<ReloadOutlined />} loading={isSentryLoading} onClick={refreshSentryStatus} shape="circle" style={{ marginLeft: '1rem' }} />
            </h2>
            {getMonitoringItemsRows(monitoringItems.sentry, isSentryLoading)}
        </Flex>
    );
}